import * as Highcharts from 'highcharts';
import Exporting from 'highcharts/modules/exporting';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { MarketInfoLabel } from './utility';
import { convertData } from '@/stores/transformData/transformData';
import { isEmpty, map, zip } from 'lodash'

import './MarketIndexChart.css'

// Initialize exporting module.
Exporting(Highcharts);

const now = new Date();
const year = now.getFullYear();
const month = now.getMonth();
const date = now.getDate();
let minTime = new Date(year, month, date, 8, 59, 59).getTime();
let maxTime = new Date(year, month, date, 15, 0, 0).getTime();

function MarketIndexChart({ history, code }: { history: any, code: any }) {
    const refChart = useRef<any>(null)
    const refCacheDataChart = useRef<any[]>([])
    const maxSeries = useRef(0)
    const minSeries = useRef(0)

    const [chartReady, setChartReady] = useState(false)
    // const [previousShareTraded, setPreviousShareTraded] = useState(0)

    const chartId = `container-chart-in-day-${code}`

    const chartTitle = {
        text: '',
        y: 30,
        floating: true,
        style: {
            color: '#6E6C6C',
            fontSize: 12
        }
    }

    const tooltipStyle = {
        fontFamily: 'Helvetica Neue Regular',
        fontWeight: '200',
        color: '#fff',
    }

    const labelStyleX = {
        color: '#939090',
        //fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
        fontFamily: 'Helvetica Neue Regular',
        fontWeight: '200',
        fontSize: '10px',
    };

    const labelStyle = {
        color: '#939090',
        //fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
        fontFamily: 'Helvetica Neue Regular',
        fontWeight: '200',
        fontSize: '0.60rem',
    };

    // const getColorIndexChartByTheme = (defaultTheme: any) => {
    //     let colorIncrease = `theme_${defaultTheme}_index_chart_increase`
    //     let colorReduce = `theme_${defaultTheme}_index_chart_reduce`
    //     let colorRef = `theme_${defaultTheme}_index_chart_ref`
    //     let colorVolumn = `theme_${defaultTheme}_index_chart_volumn`
    //     return {
    //         colorIncrease, colorReduce, colorRef, colorVolumn
    //     }
    // }

    // const { colorIncrease, colorReduce, colorRef, colorVolumn } = getColorIndexChartByTheme('dark')

    const chart = {
        marginTop: 8,
        marginRight: 10,
        marginLeft: 10,
        height: 108,
        // width: 375,
        plotBorderWidth: 0.2,
        spacing: [0, 5, 0, 5],
        alignTicks: false,
        backgroundColor: '#282b30',
        style: {
            'width': 'calc(100vw / 5 - (4 * 5px))',
            'min-width': '380px',
        } as any
    }

    const prepareChartData = (history: any) => {
        // At the start of day, history could be an empty object.
        const _history = !isEmpty(history)
            ? history
            : {
                formattedtime: [],
                volume: [],
                close: []
            };

        let length = _history.formattedtime && _history.formattedtime.length
        if (length > 0) {
            let timeSeries: any = []
            let closeSeries: any = []
            let volumeSeries: any = []
            map(_history.formattedtime, (timeString, index) => {
                let [hour, min] = map(timeString.split(':'), part =>
                    parseInt(part, 10)
                );
                let time = new Date(year, month, date, hour, min).getTime();
                if (time >= minTime && time <= maxTime) {
                    timeSeries.push(time)
                    closeSeries.push(_history.close[index])
                    volumeSeries.push(_history.volume[index])
                }
                else {
                }
            });
            return {
                indexSeries: zip(timeSeries, closeSeries),
                tradingRateSeries: zip(timeSeries, volumeSeries)
            };
        }
        return {
            indexSeries: zip([], []),
            tradingRateSeries: zip([], [])
        };
    }

    const updateChartData = (data: any) => {
        refCacheDataChart.current = [];
        let { indexSeries, tradingRateSeries } = prepareChartData(data);
        refChart?.current?.series[0].setData(indexSeries);
        refChart?.current?.series[1].setData(tradingRateSeries);
    }

    const updateMinMaxAndPriorMarketIndex = () => {
        const priorMarketIndex = history?.reference[0] ? history?.reference[0] : 109
        let max = null
        let min = null
        let vMax = 0
        let vMin = 0

        max = priorMarketIndex * 1 + 0.05;
        min = priorMarketIndex * 1 - 0.05;
        if (history && history.close && history.close.length > 0) {
            const { close, volume } = history;
            const maxChart = Math.max.apply(Math, close);
            const minChart = Math.min.apply(Math, close);
            if (maxChart > priorMarketIndex)
                max = maxChart * 1 + 0.05;
            if (minChart < priorMarketIndex)
                min = minChart * 1 - 0.05;

            const vMaxChart = Math.max.apply(Math, volume);
            const vMinChart = Math.min.apply(Math, volume);
            vMax = vMaxChart * 1 + 10e2
            vMin = vMinChart * 1 - 10e2 < 0 ? 0 : vMinChart * 1 - 10e3
            // console.log('ha_check_vMax', { vMax, vMin, vMinChart });
        }
        maxSeries.current = max
        minSeries.current = min

        if (refChart?.current) {
            refChart?.current.update({
                yAxis: [
                    {
                        title: {
                            text: ''
                        },
                        // gridLineColor: false,
                        labels: {
                            enabled: false
                        },
                        plotLines: [
                            {
                                id: 'priorMarketIndex',
                                value: priorMarketIndex,
                                width: 0.6,
                                dashStyle: 'LongDash',
                                color: "#ffcc00",
                            }
                        ],
                        max: max,
                        min: min,
                    },
                    {
                        startOnTick: false,
                        title: {
                            text: ''
                        },
                        gridLineColor: '',
                        labels: {
                            enabled: false,
                            style: labelStyle
                        },
                        opposite: true,
                    }
                ],
                series: [
                    {
                        threshold: priorMarketIndex,
                    },
                    {
                        max: vMax,
                        min: vMin,
                    }
                ]
            })
        }
    }

    // const changeMaxMin = (currentIndex: number) => {
    //     currentIndex = currentIndex * 1
    //     if (maxSeries?.current) {
    //         let max = maxSeries.current * 1
    //         if (currentIndex > max) {
    //             max = currentIndex + 0.05
    //             refChart?.current?.yAxis[0].update({
    //                 max: max,
    //             });
    //             maxSeries.current = max
    //         }
    //     }
    //     if (minSeries?.current) {
    //         let min = minSeries.current * 1
    //         if (currentIndex < min) {
    //             min = currentIndex - 0.05
    //             refChart?.current?.yAxis[0].update({
    //                 min: min,
    //             });
    //             minSeries.current = min

    //         }
    //     }
    // }

    // const _checkIndexTimeForUpdatePoint = (marketInfo: any, dataSeries: any) => {
    //     let [hour, min] = map(
    //         marketInfo.tradingTime.split(':'),
    //         part => parseInt(part, 10)
    //     );
    //     let time = new Date(year, month, date, hour, min).getTime();
    //     let volume;
    //     let data: { indexSeries: any[], tradingRateSeries: any[] } = { indexSeries: [], tradingRateSeries: [] };
    //     let checkIndexTime: number | undefined = -1;
    //     if (time < minTime || time > maxTime) {
    //         checkIndexTime = undefined
    //         console.log('market.index.item.js.:_checkIndexTimeForUpdatePoint().: Not update time.:hour, min=', hour, min)
    //     }
    //     else {
    //         if (dataSeries && dataSeries.length > 0 && time) {
    //             dataSeries.map(function (item: any, index: number) {
    //                 if (item.x == time) {
    //                     checkIndexTime = index;
    //                 }
    //             })
    //         }
    //         if (checkIndexTime >= 0) {
    //             volume = marketInfo.totalShareTraded !== previousShareTraded ? marketInfo.totalShareTraded * 1 - previousShareTraded : dataSeries[checkIndexTime].y;
    //             data = {
    //                 indexSeries: [time, marketInfo.marketIndex * 1],
    //                 tradingRateSeries: [time, volume]
    //             };
    //         }
    //         else {
    //             volume = marketInfo.totalShareTraded * 1 - previousShareTraded;
    //             // volume = marketInfo.totalShareTraded * 1;
    //             setPreviousShareTraded(marketInfo.totalShareTraded * 1)
    //             data = {
    //                 indexSeries: [time, marketInfo.marketIndex * 1],
    //                 tradingRateSeries: [time, volume]
    //             };
    //         }
    //     }
    //     return {
    //         indexTime: checkIndexTime,
    //         data: data,
    //     }
    // }

    // const add = () => {
    //     let data = concat([], refCacheDataChart);
    //     refCacheDataChart.current = [];
    //     if (data && data.length > 0) {
    //         data.map(function (item) {
    //             let checkIndex = _checkIndexTimeForUpdatePoint(item, refChart?.current?.series[0].data)
    //             let indexTime = checkIndex.indexTime
    //             if (indexTime) {
    //                 let indexSeries = checkIndex?.data?.indexSeries
    //                 let tradingRateSeries = checkIndex?.data?.tradingRateSeries
    //                 changeMaxMin(indexSeries[1])
    //                 if (indexTime >= 0) {
    //                     refChart?.current?.series[0].data[indexTime].update(indexSeries, false);
    //                     refChart?.current?.series[1].data[indexTime].update(tradingRateSeries, false);
    //                 }
    //                 else {
    //                     refChart?.current?.series[0].addPoint(indexSeries, true);
    //                     refChart?.current?.series[1].addPoint(tradingRateSeries, true);
    //                 }
    //             }
    //         })
    //         refChart?.current?.redraw();
    //     }
    // }

    // const addPointToChart = (marketInfo: any) => {
    //     refCacheDataChart?.current.push(marketInfo);
    //     addData();
    // };

    // const throttleAddPointToChart = throttle(addPointToChart, 0);
    // const addData = throttle(add, 5e3);

    useEffect(() => {
        const priorMarketIndex = history?.reference[0] ? history?.reference[0] : 109
        let max = null, min = null

        max = priorMarketIndex * 1 + 0.05;
        min = priorMarketIndex * 1 - 0.05;

        refChart.current = Highcharts.chart(chartId, {
            // options - see https://api.highcharts.com/highcharts
            chart,
            title: chartTitle,
            credits: { enabled: false },
            legend: { enabled: false },
            exporting: { enabled: false },
            tooltip: {
                useHTML: true,
                shared: true,
                snap: 0,
                // backgroundColor: null,
                borderWidth: 0,
                followPointer: true,
                formatter: function () {
                    let time = moment(this.x).format('HH:mm:ss');
                    if (this?.points?.length === 2) {
                        (window as any).hoveringPoint = this;
                        return `<div class="block-tooltip" style="border:1px solid ${this.points[0].color};" >
						<span style="color:black">${time}</span>
						<br/>
                        <span style="color:black">${code}:</span>
						<span style="color:${this.points[0].color};">
							${convertData.formatAccounting(this?.points[0].y && Math.abs(this?.points[0].y), 2)}
							(${convertData.formatAccounting(this?.points[0].y && Math.abs(this?.points[0].y - Math.abs((this?.points[0]?.series?.options as any)?.threshold)), 2)})
						</span>
						<br/>
						<span style="color:black">${MarketInfoLabel('vi').charts.volume}</span>
						<span style="color:black">
							${convertData.formatAccounting(this.points[1].y && Math.abs(this.points[1].y))}
						</span>
						</div>`
                    }
                },
                style: tooltipStyle,
            },

            xAxis: {
                type: 'datetime',
                labels: {
                    formatter() {
                        return moment(this.value).format('H') + 'h';
                    },
                    style: labelStyleX,
                },
                gridLineWidth: 0.5,
                gridLineColor: '#444',
                tickLength: 0,
                min: minTime,
                max: maxTime,
                tickInterval: 3.6e6, // 1 hour
                offset: -8,
                lineWidth: 0,
            },

            yAxis: [
                {
                    // title: {
                    //     text: ''
                    // },
                    // gridLineColor: false,
                    labels: {
                        enabled: false
                    },
                    plotLines: [
                        {
                            id: 'priorMarketIndex',
                            value: priorMarketIndex,
                            width: 0.6,
                            dashStyle: 'LongDash',
                            color: "#ffcc00",
                        }
                    ],
                    max: max,
                    min: min,
                    gridLineColor: '#444',
                },
                {
                    title: {
                        text: ''
                    },
                    gridLineColor: '',
                    labels: {
                        enabled: false,
                        style: labelStyle
                    },
                    opposite: true,
                    endOnTick: false,
                    maxPadding: 0.5,
                    startOnTick: false,
                }
            ],

            plotOptions: {
                series: {
                    animation: false,
                    lineWidth: 1,
                    color: '#27ff8c',
                    // color: colorIncrease,
                    // negativeColor: colorReduce,
                    negativeColor: '#ff6969',
                    opacity: 0.6,
                    states: {
                        hover: {
                            lineWidth: 1
                        }
                    }
                },

            },
            series: [
                {
                    id: 'Indexes',
                    type: 'line',
                    data: [],
                    step: 'left',
                    zIndex: 2,
                    threshold: priorMarketIndex,
                    color: '#00ff4c',
                    negativeColor: '#ff6969',
                    marker: {
                        radius: 3
                    },
                },
                {
                    type: 'column',
                    data: [],
                    yAxis: 1,
                    color: '#60a0ff',
                    zIndex: 1,
                    pointStart: 0,
                }
            ]
        }, () => { setChartReady(true) });
    }, [])

    useEffect(() => {
        if (chartReady === true) {
            updateMinMaxAndPriorMarketIndex()
            updateChartData(history)
        }
    }, [history, chartReady])

    return (
        <div className='chart-market-index' id={chartId} >MarketIndexChart</div>
    )
}

export default MarketIndexChart